/** Performance summaries — /attacking-match-summary/<code> and
 *  /defending-match-summary/<code> (Phase 4 of the port, the flagship).
 *
 * One component, two faces: the route names the pageType and everything
 * else follows the Jinja pair (_base.html + attacking.html +
 * defending.html). The shell is React (header cluster, toolbar, cards,
 * field, timeline scaffolding); the visualizations render through the
 * 1:1-ported imperative modules (ScoreRing, MiniBarChart, ScoreTimeline,
 * MomentFieldMap) into the same element ids the Jinja page used, driven
 * from one load effect per (team, phase) exactly as loadTeamData was.
 *
 * Data:
 *   GET /api/v1/performance-summary/page-context/<code>   (was Jinja context)
 *   GET /api/v1/game-match-summary/games                  (selector dropdown)
 *   GET /api/v1/game-match-summary/tactical-score/<code>
 *   attacking: GET /api/v1/attacking-match-summary/moments/<code>?team=&phase=
 *              (x2 — viewing team and opponent)
 *   defending: GET /api/v1/game-match-summary/moments/<code>[?phase=]
 *              GET /api/v1/defending-match-summary/moments/<code>?team=&phase=
 *
 * Moment/card/CTA navigations stay on the LEGACY /game-match-summary/…
 * addresses on purpose: Flask's shell there auto-switches org for
 * deep-links and 302s to the React match page preserving the query —
 * behavior this page inherits by linking it, not by reimplementing it.
 *
 * Parity departures, each deliberate:
 * - The breadcrumb ("Season / Attacking") is gone — same call the match
 *   page made (its classes were styled nowhere; the sidebar and the crest
 *   already say it).
 * - PerformanceSummary.loadMatchContext is not ported: it wrote to
 *   score-badge/match-context elements that existed on NEITHER Jinja page
 *   — a silent no-op since the consolidated toolbar landed.
 * - A foreign game code triggers the SPA's alignToGame org-switch (the
 *   GameMatchSummary/InsightsGame convention) instead of a bare 404.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { trackEvent } from '../../lib/analytics'
import { API_BASE } from '../../lib/api'
import { servedFps } from '../../lib/frameRate'
import { alignToGame } from '../../lib/orgAlign'
import { useSession } from '../../lib/session'
import { ScoreRing } from '../../lib/viz/scoreRing'
import * as MiniBarChart from './miniBarChart'
import * as MomentFieldMap from './momentFieldMap'
import * as ScoreTimeline from './scoreTimeline'
import type { MomentLike } from './momentUtils'
import { averageScore, pressBands, pressMomentScore, pressThirdCounts } from './pressScores'
import type { TimeRange } from './miniBarChart'
import './legacy-css/design-tokens.css'
import './legacy-css/performance-summary.css'
import './legacy-css/metric-card.css'
import './legacy-css/stat-card.css'
import './legacy-css/zone-stats.css'
import './legacy-css/score-ring.css'
import './legacy-css/score-timeline.css'
import './legacy-css/moment-field-map.css'
import './legacy-css/moment-popover.css'
import './legacy-css/data-tooltip.css'
import './legacy-css/segmented-control.css'
import './legacy-css/page-toolbar.css'
import './legacy-css/time-filter.css'
import './legacy-css/mini-bar-chart.css'

type PageType = 'attacking' | 'defending'

type Ctx = {
  game_code: string
  home_team: string
  away_team: string
  fixture_id: number | null
  team_logo_url: string | null
  team_name: string | null
  definitions: Record<string, string>
}

type GameRow = { game_code: string; home_team?: string; away_team?: string; date?: string }

type TacticalPoint = { frame: number; possession?: number; tactical_score?: number }

const j = (r: Response) => r.json()

/** null = full, 1 = first half, 2 = second half — for the phase= param. */
function timeRangeToPhase(timeRange: TimeRange): 1 | 2 | null {
  if (!timeRange || timeRange.preset === 'full') return null
  if (timeRange.preset === '1h' || (timeRange.from === 0 && timeRange.to === 45)) return 1
  if (timeRange.preset === '2h' || (timeRange.from === 45 && timeRange.to === 90)) return 2
  return null // Custom ranges are chart-side filters, not API phases
}

/** The pitch SVG the moment_field_map macro rendered, same ids. */
function FieldMap({ idPrefix, zoneStats, zones, unplaced = 0 }: {
  idPrefix: string
  zoneStats: 'single' | 'triple'
  zones: (number | string)[]
  /** Presses on the map that no third holds (no x or no half). */
  unplaced?: number
}) {
  return (
    <div className="moment-field-map" id={`${idPrefix}-map`}>
      <div className="moment-field-map__field-wrapper">
        <div className="moment-field-map__pitch" id={`${idPrefix}-pitch`}>
          <div className="moment-field-map__labels">
            <span className="moment-field-map__label moment-field-map__label--left"
                  id={`${idPrefix}-left-label`} />
            <span className="moment-field-map__label moment-field-map__label--right"
                  id={`${idPrefix}-right-label`} />
          </div>

          {zoneStats === 'single' && (
            <div className="field-stats-overlay field-stats-overlay--single"
                 id={`${idPrefix}-zone-stats`}>
              <div className="field-stats-overlay__stat">
                <span className="field-stats-overlay__label">Attacking Entries</span>
                <span className="field-stats-overlay__value">{zones[0] ?? '-'}</span>
              </div>
            </div>
          )}
          {zoneStats === 'triple' && (
            <div className="field-stats-overlay field-stats-overlay--triple"
                 id={`${idPrefix}-zone-stats`}>
              {(['Defensive Third', 'Mid Press', 'High Press'] as const).map((label, i) => (
                <div className="field-stats-overlay__stat" key={label}>
                  <span className="field-stats-overlay__label">{label}</span>
                  <span className="field-stats-overlay__value">{zones[i] ?? '-'}</span>
                </div>
              ))}
              {unplaced > 0 && (
                <div className="field-stats-overlay__note" data-testid="unplaced-presses">
                  {unplaced} not in a third (no position or half)
                </div>
              )}
            </div>
          )}

          {/* Full field SVG, horizontal (105m x 68m; 10 units per meter). */}
          <svg className="moment-field-map__svg" id={`${idPrefix}-svg`}
               viewBox="0 0 1050 680" preserveAspectRatio="xMidYMid meet">
            <line x1="525" y1="0" x2="525" y2="680" stroke="#5B6472" strokeWidth="2" />
            <circle cx="525" cy="340" r="92" fill="none" stroke="#5B6472" strokeWidth="2" />
            <circle cx="525" cy="340" r="4" fill="#5B6472" />
            <rect x="0" y="138" width="165" height="404" fill="none" stroke="#5B6472" strokeWidth="2" />
            <rect x="0" y="248" width="55" height="184" fill="none" stroke="#5B6472" strokeWidth="2" />
            <circle cx="110" cy="340" r="4" fill="#5B6472" />
            <path d="M 165 266 A 92 92 0 0 1 165 414" fill="none" stroke="#5B6472" strokeWidth="2" />
            <rect x="885" y="138" width="165" height="404" fill="none" stroke="#5B6472" strokeWidth="2" />
            <rect x="995" y="248" width="55" height="184" fill="none" stroke="#5B6472" strokeWidth="2" />
            <circle cx="940" cy="340" r="4" fill="#5B6472" />
            <path d="M 885 266 A 92 92 0 0 0 885 414" fill="none" stroke="#5B6472" strokeWidth="2" />
            <g id={`${idPrefix}-dots`} className="moment-field-map__dots-group" />
          </svg>

          <div className="moment-field-map__empty" id={`${idPrefix}-empty`}
               style={{ display: 'none' }}>
            <i className="ph ph-map-pin moment-field-map__empty-icon" />
            <span>No moments to display</span>
          </div>

          <div className="moment-popover" id={`${idPrefix}-popover`} />
        </div>
      </div>
    </div>
  )
}

/** The dual_score_timeline macro's markup, same ids. */
function DualTimeline({ idPrefix, label, tooltip, glossaryLink }: {
  idPrefix: string
  label: string
  tooltip: string | undefined
  glossaryLink: string
}) {
  return (
    <div className="dual-score-timeline score-timeline--amber" id={`${idPrefix}-dual-timeline`}>
      <div className="dual-score-timeline__row">
        <span className="dual-score-timeline__team" id={`${idPrefix}-top-team`} />
        <div className="dual-score-timeline__chart score-timeline__chart" id={`${idPrefix}-top-chart`}>
          <svg className="score-timeline__svg" id={`${idPrefix}-top-svg`} />
          <div className="score-timeline__dots" id={`${idPrefix}-top-dots`} />
          <div className="score-timeline__scrubber" id={`${idPrefix}-top-scrubber`} />
          <div className="score-timeline__tooltip" id={`${idPrefix}-top-tooltip`} />
          <div className="data-tooltip" id={`${idPrefix}-top-moment-tooltip`} />
        </div>
      </div>
      <div className="dual-score-timeline__axis">
        <div className="dual-score-timeline__label-group">
          <span className="dual-score-timeline__label">{label}</span>
          {tooltip && (
            <>
              <button className="dual-score-timeline__info-btn" type="button"
                      aria-label={`More info about ${label}`}>
                <i className="ph ph-info" />
              </button>
              <div className="dual-score-timeline__info-tooltip">
                <div className="dual-score-timeline__info-tooltip-title">{label}</div>
                <div className="dual-score-timeline__info-tooltip-text">{tooltip}</div>
                <a href={glossaryLink} className="dual-score-timeline__info-tooltip-link">
                  See in glossary
                </a>
              </div>
            </>
          )}
        </div>
        <div className="dual-score-timeline__markers score-timeline__markers"
             id={`${idPrefix}-markers`} />
      </div>
      <div className="dual-score-timeline__row dual-score-timeline__row--bottom">
        <span className="dual-score-timeline__team" id={`${idPrefix}-bottom-team`} />
        <div className="dual-score-timeline__chart dual-score-timeline__chart--static score-timeline__chart"
             id={`${idPrefix}-bottom-chart`}>
          <svg className="score-timeline__svg" id={`${idPrefix}-bottom-svg`} />
          <div className="score-timeline__dots" id={`${idPrefix}-bottom-dots`} />
          <div className="data-tooltip" id={`${idPrefix}-bottom-moment-tooltip`} />
        </div>
      </div>
    </div>
  )
}

/** The time_filter macro: 1H/2H/Full presets + the interval dropdown. */
function TimeFilter({ onChange }: { onChange: (r: TimeRange) => void }) {
  const [active, setActive] = useState<'1h' | '2h' | 'full' | 'range'>('full')
  const [open, setOpen] = useState(false)
  const [range, setRange] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('click', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const pick = (preset: '1h' | '2h' | 'full') => {
    setActive(preset)
    setRange(null)
    setOpen(false)
    if (preset === '1h') onChange({ from: 0, to: 45, preset: '1h' })
    else if (preset === '2h') onChange({ from: 45, to: 90, preset: '2h' })
    else onChange({ from: 0, to: 90, preset: 'full' })
  }

  const pickRange = (r: string) => {
    const [from, to] = r.split('-').map(Number) as [number, number]
    setActive('range')
    setRange(r)
    setOpen(false)
    onChange({ from, to, preset: 'range' })
  }

  const item = (r: string, label: string) => (
    <button key={r} className={`time-filter__menu-item${range === r ? ' selected' : ''}`}
            onClick={() => pickRange(r)}>
      <span>{label}</span>
      <i className="ph ph-check time-filter__check" />
    </button>
  )

  return (
    <div className="time-filter" id="match-time-filter" ref={rootRef}>
      <div className="segmented-control segmented-control--clips">
        <button className={`segment${active === '1h' ? ' active' : ''}`}
                onClick={() => pick('1h')}>1H</button>
        <button className={`segment${active === '2h' ? ' active' : ''}`}
                onClick={() => pick('2h')}>2H</button>
        <button className={`segment${active === 'full' ? ' active' : ''}`}
                onClick={() => pick('full')}>Full</button>
        <button className={`segment time-filter__dropdown-trigger${active === 'range' ? ' active' : ''}`}
                aria-expanded={open}
                onClick={(e) => { e.stopPropagation(); setOpen(!open) }}>
          <span>
            {range ? `${range}'` : 'Range'}
          </span>
          <i className="ph ph-caret-down time-filter__trigger-icon" />
        </button>
      </div>
      <div className={`time-filter__dropdown${open ? ' open' : ''}`}
           id="match-time-filter-dropdown">
        <div className="time-filter__grid">
          <div className="time-filter__column">
            <span className="time-filter__column-header">1H</span>
            {item('0-15', "0-15'")}
            {item('15-30', "15-30'")}
            {item('30-45', "30-45'")}
          </div>
          <div className="time-filter__column">
            <span className="time-filter__column-header">2H</span>
            {item('45-60', "45-60'")}
            {item('60-75', "60-75'")}
            {item('75-90', "75-90'")}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function PerformanceSummary({ pageType }: { pageType: PageType }) {
  const { gameCode = '' } = useParams<{ gameCode: string }>()
  const [search] = useSearchParams()
  const session = useSession()

  useEffect(() => {
    // Same trend as the match page and video viewer; `surface` names
    // which face of the summary was read.
    if (gameCode) {
      trackEvent('Game Viewed',
                 { game_code: gameCode, surface: `${pageType}_summary` })
    }
  }, [gameCode, pageType])

  const teams = gameCode.split('@')
  const awayTeam = teams[0] ?? gameCode
  const homeTeam = teams[1] ?? teams[0] ?? gameCode

  // URL param first, then the org's own team if it is in the match,
  // otherwise the away team — exactly the Jinja bootstrap.
  const teamParam = search.get('team')
  const initialIndex = teamParam
    ? (teamParam.toUpperCase() === homeTeam.toUpperCase() ? 1 : 0)
    : (session.org?.code === homeTeam ? 1 : 0)

  const [viewingIndex, setViewingIndex] = useState<0 | 1>(initialIndex as 0 | 1)
  const viewingTeam = viewingIndex === 0 ? awayTeam : homeTeam
  const otherTeam = viewingIndex === 0 ? homeTeam : awayTeam

  const [ctx, setCtx] = useState<Ctx | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [loading, setLoading] = useState(true)
  const [games, setGames] = useState<GameRow[]>([])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [timeRange, setTimeRange] = useState<TimeRange>(null)
  const [directionNotSet, setDirectionNotSet] = useState<string | null>(null)
  const [zones, setZones] = useState<(number | string)[]>(['-', '-', '-'])
  // presses on the map that no third holds (no x or no half)
  const [unplacedPresses, setUnplacedPresses] = useState(0)
  const [statValues, setStatValues] = useState<{ passes: number | string; runs: number | string }>(
    { passes: '--', runs: '--' })
  const selectorRef = useRef<HTMLDivElement>(null)

  const currentPhase = timeRangeToPhase(timeRange)
  const fieldMapId = pageType === 'attacking' ? 'spatial-xg-map' : 'def-spatial-xg-map'
  const timelineId = pageType === 'attacking' ? 'attacking-timeline' : 'pressing-timeline'
  const gms = (path: string) => `/game-match-summary/${gameCode}${path}`

  // Page context — the Jinja render context (also the org-ownership gate:
  // a foreign game 404s, and the SPA convention is to follow the code
  // into the club that owns it).
  useEffect(() => {
    let cancelled = false
    const load = async (retried: boolean) => {
      const r = await fetch(
        `${API_BASE}/api/v1/performance-summary/page-context/${encodeURIComponent(gameCode)}`,
        { credentials: 'same-origin' })
      if (cancelled) return
      if (r.ok) {
        const body = await r.json()
        if (!cancelled && body.success) setCtx(body.data as Ctx)
        return
      }
      if (r.status === 404 && !retried) {
        const { switched } = await alignToGame(gameCode)
        if (switched) { window.location.reload(); return }
      }
      if (!cancelled) setNotFound(true)
    }
    void load(false)
    return () => { cancelled = true }
  }, [gameCode])

  // Games dropdown (best-effort, as upstream).
  useEffect(() => {
    fetch(`${API_BASE}/api/v1/game-match-summary/games`, { credentials: 'same-origin' })
      .then(j)
      .then((data) => { if (data.success) setGames(data.games as GameRow[]) })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('click', onDoc)
    return () => document.removeEventListener('click', onDoc)
  }, [])

  /** The attacking page's loadTeamData, verbatim in behavior. */
  const loadAttacking = useCallback(async () => {
    const team = viewingTeam
    const opponent = otherTeam
    const teamId = viewingIndex
    const opponentId = teamId === 1 ? 0 : 1
    const phaseParam = currentPhase ? `&phase=${currentPhase}` : ''
    const b = API_BASE

    try {
      const [momentsResult, opponentMomentsResult, tacticalResult] = await Promise.all([
        fetch(`${b}/api/v1/attacking-match-summary/moments/${gameCode}?team=${team}${phaseParam}`,
              { credentials: 'same-origin' }).then(j),
        fetch(`${b}/api/v1/attacking-match-summary/moments/${gameCode}?team=${opponent}${phaseParam}`,
              { credentials: 'same-origin' }).then(j),
        fetch(`${b}/api/v1/game-match-summary/tactical-score/${gameCode}`,
              { credentials: 'same-origin' }).then(j),
      ])

      // RULED B (ledger 26): no direction, no attacking surface — the API
      // refuses rather than orienting the map by a guess, and this page
      // says why instead of rendering empty charts.
      if (momentsResult.error === 'direction_not_set') {
        setDirectionNotSet(momentsResult.message
          || 'Set the attack direction in Mark Events first.')
        setLoading(false)
        return
      }

      let attackingMoments: MomentLike[] = []
      let spatialXgMoments: MomentLike[] = []
      let attackingScore: number | null = null
      let gameFps: number | null = null

      if (momentsResult.success) {
        const data = momentsResult.data
        gameFps = servedFps(data.fps)
        setStatValues({ passes: data.progressive_passes_count ?? 0,
                        runs: data.progressive_runs_count ?? 0 })
        setZones([data.chance_creation_count ?? 0])

        // Team label with attack direction indicator (no opponent label).
        MomentFieldMap.setLabels(fieldMapId, `${team} →`, '')
        MomentFieldMap.render(fieldMapId, data.spatial_xg, {
          onClick: (m) => {
            window.location.href = gms(`?from=attacking&type=box_entry_cci&frame=${m.frame}`)
          },
          normalizeForTeam: {
            teamId,
            homeTeamStartDirection: data.home_team_start_direction,
            currentPhase,
          },
          fps: gameFps,
        })
        attackingMoments = data.attacking_moments || []
        spatialXgMoments = data.spatial_xg || []
        attackingScore = data.attacking_score ?? null

        // Mini bar charts for progressive passes/runs.
        const bucketOptions = { fps: gameFps }
        const unplaced = gameFps ? {} : { note: MiniBarChart.NO_FRAME_RATE_NOTE }
        MiniBarChart.render('progressive-passes-chart',
          MiniBarChart.bucketMoments(attackingMoments, 'progressive_pass', timeRange, bucketOptions),
          unplaced)
        MiniBarChart.render('progressive-runs-chart',
          MiniBarChart.bucketMoments(attackingMoments, 'progressive_run', timeRange, bucketOptions),
          unplaced)
      }

      const opponentAttackingMoments: MomentLike[] =
        opponentMomentsResult.success ? (opponentMomentsResult.data.attacking_moments || []) : []

      let allTacticalData: TacticalPoint[] = []
      let viewingTeamData: TacticalPoint[] = []
      let opponentTeamData: TacticalPoint[] = []
      if (tacticalResult.success && tacticalResult.data) {
        allTacticalData = tacticalResult.data as TacticalPoint[]
        viewingTeamData = allTacticalData.filter((p) => p.possession === teamId)
        opponentTeamData = allTacticalData.filter((p) => p.possession === opponentId)
      }

      if (attackingScore !== null && attackingScore !== undefined) {
        ScoreRing.render('attacking-main-ring', attackingScore, { size: 'lg' })
      } else {
        ScoreRing.renderEmpty('attacking-main-ring', { size: 'lg' })
      }

      // WHOOP-style interval chart from spatial xG moments: avg score per
      // 15-minute bucket, backfilled with the overall score.
      const scores = MiniBarChart.intervalScores(spatialXgMoments, gameFps, attackingScore)
      MiniBarChart.renderScores('attacking-main-chart', scores, {
        timeRange, ...(gameFps ? {} : { note: MiniBarChart.NO_FRAME_RATE_NOTE }) })

      const totalFrames = allTacticalData.length > 0
        ? Math.max(...allTacticalData.map((p) => p.frame), 54000) : 54000
      ScoreTimeline.renderDual(timelineId,
        {
          team,
          data: viewingTeamData as ScoreTimeline.TimelinePoint[],
          moments: attackingMoments,
          onClick: (frame) => { window.location.href = gms(`?from=attacking&frame=${frame}`) },
          onMomentClick: (m) => {
            window.location.href = gms(`?from=attacking&type=${m.type}&moment=${m.id}&frame=${m.frame}`)
          },
        },
        { team: opponent, data: opponentTeamData as ScoreTimeline.TimelinePoint[],
          moments: opponentAttackingMoments },
        { totalFrames, color: 'amber', scoreField: 'tactical_score',
          minScore: 0, maxScore: 1, neutralDotColor: 'neutral',
          fps: gameFps })

      setLoading(false)
    } catch {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameCode, viewingIndex, currentPhase, timeRange])

  /** The defending page's loadTeamData, verbatim in behavior. */
  const loadDefending = useCallback(async () => {
    const team = viewingTeam
    const opponent = otherTeam
    const isHome = viewingIndex === 1
    const teamIndex = viewingIndex
    const phaseParam = currentPhase ? `&phase=${currentPhase}` : ''
    const b = API_BASE

    try {
      const [tacticalResult, momentsResult, defendingResult] = await Promise.all([
        fetch(`${b}/api/v1/game-match-summary/tactical-score/${gameCode}`,
              { credentials: 'same-origin' }).then(j),
        fetch(`${b}/api/v1/game-match-summary/moments/${gameCode}${currentPhase ? '?phase=' + currentPhase : ''}`,
              { credentials: 'same-origin' }).then(j),
        fetch(`${b}/api/v1/defending-match-summary/moments/${gameCode}?team=${team}${phaseParam}`,
              { credentials: 'same-origin' }).then(j),
      ])

      let allTacticalData: TacticalPoint[] = []
      let viewingTeamPressing: TacticalPoint[] = []
      let opponentPressing: TacticalPoint[] = []
      if (tacticalResult.success && tacticalResult.data?.length > 0) {
        allTacticalData = tacticalResult.data as TacticalPoint[]
        viewingTeamPressing = allTacticalData.filter((p) => p.possession !== teamIndex)
        opponentPressing = allTacticalData.filter((p) => p.possession === teamIndex)
      }

      let pressingMoments: MomentLike[] = []
      let opponentPressingMoments: MomentLike[] = []
      if (momentsResult.success && momentsResult.moments) {
        const viewingPressing = isHome
          ? momentsResult.moments.home?.pressing : momentsResult.moments.away?.pressing
        const oppPressing = isHome
          ? momentsResult.moments.away?.pressing : momentsResult.moments.home?.pressing
        type RawPressing = { frame: number; pressing_index?: number | null;
                             score?: number | null; score_scale?: string | null;
                             score_bands?: { okay: number; good: number } | null;
                             id?: string | number; x?: number; y?: number;
                             type?: string; player?: string | number;
                             phase?: number | null }
        pressingMoments = ((viewingPressing || []) as RawPressing[]).map((m) => ({
          frame: m.frame, score: pressMomentScore(m), score_scale: m.score_scale,
          score_bands: m.score_bands,
          id: m.id, x: m.x, y: m.y, type: m.type || 'pressing', player: m.player,
          phase: m.phase,
        }))
        opponentPressingMoments = ((oppPressing || []) as RawPressing[]).map((m) => ({
          frame: m.frame, score: pressMomentScore(m),
          id: m.id, type: m.type || 'pressing', player: m.player,
        }))
      }

      // Defending score = average score over the scored pressing moments,
      // coloured on the bands the server sends with the pressing model's
      // presses, on no band where such presses come without them (the
      // neutral state), and on the pressing index's for a legacy game.
      const defendingScore = averageScore(pressingMoments)
      const bands = pressBands(pressingMoments)
      const servedBands = bands === 'none' ? null : bands
      const unbanded = bands === 'none'
      const defendingBands = servedBands
        ? { variant: 'dynamic', thresholds: servedBands } : { variant: 'pressing' }
      if (defendingScore !== null && defendingScore !== undefined && !unbanded) {
        ScoreRing.render('defending-main-ring', defendingScore, { size: 'lg', ...defendingBands })
      } else {
        ScoreRing.renderEmpty('defending-main-ring', { size: 'lg' })
      }

      // WHOOP-style interval chart.
      const gameFps = servedFps(defendingResult.success ? defendingResult.data?.fps : null)
      const scores = MiniBarChart.intervalScores(pressingMoments, gameFps, defendingScore)
      MiniBarChart.renderScores('defending-main-chart', unbanded ? scores.map(() => null) : scores,
        { ...defendingBands, timeRange,
          ...(gameFps ? {} : { note: MiniBarChart.NO_FRAME_RATE_NOTE }) })

      // Zone counts: the thirds in the viewing team's direction.
      // No direction: dashes, not 0 0 0 — nothing could be placed. Presses
      // with no x or half are on the map but in no third; say how many.
      const counts = pressThirdCounts(pressingMoments,
        (defendingResult.success && defendingResult.data?.home_team_start_direction) || null,
        isHome,
        (defendingResult.success && defendingResult.data?.zone_length_m) || undefined)
      setZones(counts ? counts.thirds : ['-', '-', '-'])
      setUnplacedPresses(counts?.unplaced ?? 0)

      // Team label with defend direction indicator (no opponent label).
      MomentFieldMap.setLabels(fieldMapId, `← ${team}`, '')
      MomentFieldMap.render(fieldMapId, pressingMoments, {
        onClick: (m) => {
          window.location.href = gms(`?from=defending&filter=high_press&frame=${m.frame}`)
        },
        fps: gameFps,
        // the served bands for marked presses, the neutral ring for marked
        // presses served without them; a legacy game's keep the default bands
        ...(servedBands ? { variant: 'dynamic', thresholds: servedBands } : {}),
        ...(unbanded ? { neutral: true } : {}),
      })

      const maxFrame = allTacticalData.length > 0
        ? Math.max(...allTacticalData.map((p) => p.frame), 54000) : 54000
      ScoreTimeline.renderDual(timelineId,
        {
          team,
          data: viewingTeamPressing.map((p) => ({
            frame: p.frame, tactical_score: p.tactical_score || 0 })),
          moments: pressingMoments,
          onClick: (frame) => { window.location.href = gms(`?from=defending&frame=${frame}`) },
          onMomentClick: (m) => {
            window.location.href = gms(`?from=defending&filter=high_press&moment=${m.id}`)
          },
        },
        {
          team: opponent,
          data: opponentPressing.map((p) => ({
            frame: p.frame, tactical_score: p.tactical_score || 0 })),
          moments: opponentPressingMoments,
        },
        { totalFrames: maxFrame, color: 'amber', scoreField: 'tactical_score',
          minScore: 0, maxScore: 1, neutralDotColor: 'neutral',
          fps: gameFps })

      setLoading(false)
    } catch {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameCode, viewingIndex, currentPhase, timeRange])

  // One load per (team, phase), once the context proved ownership — the
  // Jinja page's loadTeamData(viewingTeam) call sites collapsed into an
  // effect. The imperative renders need the grid mounted, which ctx
  // gates, so this runs strictly after it.
  useEffect(() => {
    if (!ctx || directionNotSet) return
    setLoading(true)
    if (pageType === 'attacking') void loadAttacking()
    else void loadDefending()
  }, [ctx, pageType, directionNotSet, loadAttacking, loadDefending])

  if (notFound) {
    return (
      <div className="performance-page use-performance-colors">
        <div className="performance-content" style={{ padding: '48px 24px', textAlign: 'center' }}>
          <h2 style={{ marginBottom: 8 }}>Game not found</h2>
          <p style={{ color: '#6b7280' }}>
            This game does not exist in any club you can act for.
          </p>
        </div>
      </div>
    )
  }
  if (!ctx) return null

  const defs = ctx.definitions
  const toggleTeam = () => {
    setViewingIndex(viewingIndex === 0 ? 1 : 0)
    setLoading(true)
  }

  return (
    <div className="performance-page use-performance-colors"
         data-loading={loading || undefined}>
      {/* The Jinja header_left cluster, rendered at the top of page content
          (the Passes 8/9 convention): crest, game selector, Viewing as. */}
      <div className="header-left-custom">
        <a href="/match-room" className="header-logo-link">
          {ctx.team_logo_url ? (
            <img src={ctx.team_logo_url} alt={ctx.team_name || 'Team'}
                 className="team-logo-header" />
          ) : (
            <i className="ph-light ph-shield-star team-icon team-icon--lg" />
          )}
        </a>
        <div className="header-left-content">
          <div className="header-controls-row">
            <div className="game-selector" id="game-selector" ref={selectorRef}
                 style={{ position: 'relative' }}
                 onClick={() => { if (games.length) setDropdownOpen(!dropdownOpen) }}>
              <span className="game-selector-text" id="game-selector-text">
                {`${viewingTeam} v ${otherTeam}`}
              </span>
              <svg className="game-selector-icon" fill="none" stroke="currentColor"
                   strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              {dropdownOpen && (
                <div id="game-dropdown-menu"
                     style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0,
                              minWidth: '100%', maxWidth: 400, background: 'white',
                              border: '1px solid #e5e7eb', borderRadius: 8,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 400,
                              overflowY: 'auto', zIndex: 1000 }}>
                  {games.map((game, i) => {
                    const isCurrent = game.game_code === gameCode
                    return (
                      <div key={game.game_code}
                           style={{ padding: '12px 16px', cursor: 'pointer',
                                    borderBottom: i < games.length - 1 ? '1px solid #f3f4f6' : undefined,
                                    background: isCurrent ? '#eff6ff' : undefined,
                                    borderLeft: isCurrent ? '3px solid #3b82f6' : undefined,
                                    paddingLeft: isCurrent ? 13 : 16 }}
                           onClick={(e) => {
                             e.stopPropagation()
                             window.location.href = `/${pageType}-match-summary/${game.game_code}`
                           }}>
                        <div style={{ fontWeight: isCurrent ? 600 : 500, fontSize: 14,
                                      color: isCurrent ? '#1e40af' : '#111827' }}>
                          {game.home_team} v {game.away_team}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                          {game.date || ''}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            <button className="filter-btn filter-btn--neutral" id="view-as-btn"
                    onClick={toggleTeam}>
              Viewing as <span id="view-as-team">{viewingTeam}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="performance-content">
        {/* Consolidated toolbar: view toggle + time filter + CTA. */}
        <div className="page-toolbar">
          <div className="page-toolbar__controls">
            <div className="segmented-control segmented-control--clips" data-view-toggle>
              <a href={`/attacking-match-summary/${gameCode}`}
                 className={`segment${pageType === 'attacking' ? ' active' : ''}`}>Attacking</a>
              <a href={`/defending-match-summary/${gameCode}`}
                 className={`segment${pageType === 'defending' ? ' active' : ''}`}>Defending</a>
            </div>
            <div className="page-toolbar__divider" />
            <TimeFilter onChange={(r) => { setTimeRange(r); setLoading(true) }} />
          </div>
          <div className="page-toolbar__actions">
            <a href={gms(`?from=${pageType}`)}
               className="page-toolbar__cta page-toolbar__cta--inline">
              <i className="ph ph-play" />
              See Key Moments
            </a>
          </div>
        </div>

        {directionNotSet ? (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <h2 style={{ marginBottom: 8 }}>Attack direction not set</h2>
            <p>{directionNotSet}</p>
            <p><a href={gms('')}>Open the match view</a></p>
          </div>
        ) : (
          <>
            <div className="performance-grid">
              <div className="metrics-column">
                {pageType === 'attacking' ? (
                  <>
                    <div className="metrics-column__section metrics-column__section--primary">
                      <div className="metric-card metric-card--lg metric-card--wide metric-card--with-chart"
                           id="attacking-main-card" role="button" tabIndex={0}
                           onClick={() => {
                             window.location.href = gms('?from=attacking&filter=progressive_passes')
                           }}
                           onKeyDown={(e) => {
                             if (e.key === 'Enter') {
                               window.location.href = gms('?from=attacking&filter=progressive_passes')
                             }
                           }}>
                        <div className="metric-card__header-row">
                          <div className="score-ring" id="attacking-main-ring" />
                          <div className="metric-card__text">
                            <h3 className="metric-card__title">Attacking Effectiveness</h3>
                            {defs.attacking_effectiveness && (
                              <p className="metric-card__description">{defs.attacking_effectiveness}</p>
                            )}
                          </div>
                        </div>
                        <div className="metric-card__chart" id="attacking-main-chart" />
                      </div>
                    </div>
                    <div className="metrics-column__section metrics-column__section--secondary metrics-column__section--row">
                      <a className={`stat-card${defs.progressive_passes ? ' stat-card--with-description' : ''}`}
                         id="progressive-passes-card"
                         href={gms('?from=attacking&filter=progressive_passes')}>
                        <span className="stat-card__label">Progressive Passes</span>
                        <div className="stat-card__body">
                          <div className="stat-card__main">
                            <span className="stat-card__value" id="progressive-passes-value">
                              {statValues.passes}
                            </span>
                          </div>
                          {defs.progressive_passes && (
                            <p className="stat-card__description">{defs.progressive_passes}</p>
                          )}
                        </div>
                        <div className="stat-card__chart" id="progressive-passes-chart" />
                      </a>
                      <a className={`stat-card${defs.progressive_run ? ' stat-card--with-description' : ''}`}
                         id="progressive-runs-card"
                         href={gms('?from=attacking&filter=progressive_runs')}>
                        <span className="stat-card__label">Progressive Runs</span>
                        <div className="stat-card__body">
                          <div className="stat-card__main">
                            <span className="stat-card__value" id="progressive-runs-value">
                              {statValues.runs}
                            </span>
                          </div>
                          {defs.progressive_run && (
                            <p className="stat-card__description">{defs.progressive_run}</p>
                          )}
                        </div>
                        <div className="stat-card__chart" id="progressive-runs-chart" />
                      </a>
                    </div>
                  </>
                ) : (
                  <div className="metrics-column__section metrics-column__section--fill">
                    <div className="metric-card metric-card--lg metric-card--wide metric-card--with-chart"
                         id="defending-main-card" role="button" tabIndex={0}
                         onClick={() => {
                           window.location.href = gms('?from=defending&filter=high_press')
                         }}
                         onKeyDown={(e) => {
                           if (e.key === 'Enter') {
                             window.location.href = gms('?from=defending&filter=high_press')
                           }
                         }}>
                      <div className="metric-card__header-row">
                        <div className="score-ring" id="defending-main-ring" />
                        <div className="metric-card__text">
                          <h3 className="metric-card__title">Defending Effectiveness</h3>
                          {defs.defending_effectiveness && (
                            <p className="metric-card__description">{defs.defending_effectiveness}</p>
                          )}
                        </div>
                      </div>
                      <div className="metric-card__chart" id="defending-main-chart" />
                    </div>
                  </div>
                )}
              </div>

              <div className="visualization-column">
                {pageType === 'attacking' && (
                  <div className="zone-stats-aligned" style={{ justifyContent: 'flex-end' }}>
                    <div className="zone-stat" data-tooltip={defs.chance_creation}
                         style={{ textAlign: 'right' }}>
                      <span className="zone-stat__label">Attacking Entries</span>
                      <span className="zone-stat__value" id="chance-creation-value">
                        {zones[0] ?? '--'}
                      </span>
                    </div>
                  </div>
                )}
                <FieldMap idPrefix={fieldMapId}
                          zoneStats={pageType === 'attacking' ? 'single' : 'triple'}
                          zones={zones}
                          unplaced={pageType === 'attacking' ? 0 : unplacedPresses} />
              </div>
            </div>

            <div className="timeline-section">
              {pageType === 'attacking' ? (
                <DualTimeline idPrefix={timelineId} label="In Possession Tactical Score"
                              tooltip={defs.tactical_score}
                              glossaryLink="/glossary/#tactical-score" />
              ) : (
                <DualTimeline idPrefix={timelineId} label="Pressing Effectiveness"
                              tooltip={defs.pressing_index}
                              glossaryLink="/glossary/#pressing-index" />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
